import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

export default function Onboarding() {
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState("linking"); // linking | no_match | verify | done
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [altEmail, setAltEmail] = useState(user?.email || "");
  const [code, setCode] = useState("");
  const [debugCode, setDebugCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Auto-attempt on mount
  useEffect(() => {
    attemptAutoLink();
  }, []);

  async function attemptAutoLink() {
    setLoading(true);
    setError("");
    try {
      const { data } = await API.post("/intakeq/auto-link");
      if (data.status === "verified") {
        setStep("done");
        setTimeout(() => { window.location.href = "/"; }, 1500);
      } else {
        setStep("no_match");
      }
    } catch (err) {
      console.error("[Onboarding] Auto-link failed:", err);
      setStep("no_match");
    } finally {
      setLoading(false);
    }
  }

  async function handleAltEmailSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setDebugCode("");
    try {
      const { data } = await API.post("/intakeq/link-identity", {
        email: altEmail,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      if (data.status === "code_sent") {
        setStep("verify");
        if (data.debug_code) setDebugCode(data.debug_code);
      } else if (data.status === "already_verified") {
        setStep("done");
        setTimeout(() => { window.location.href = "/"; }, 1500);
      } else if (data.status === "not_found") {
        setError("No IntakeQ record found for that email. Please check and try again.");
      } else {
        setError(data.message || "Unable to process. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || "No IntakeQ record found for that email.");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await API.post("/intakeq/verify-code", { code });
      if (data.status === "verified") {
        setStep("done");
        setTimeout(() => { window.location.href = "/"; }, 1500);
      } else {
        setError(data.message || "Verification failed.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || "Verification failed.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSkip() {
    try {
      await API.post("/intakeq/skip-onboarding");
      window.location.href = "/";
    } catch {
      window.location.href = "/";
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4" data-testid="onboarding-page">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground font-display font-bold text-2xl shadow-lg">
            EM
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground tracking-tight">Welcome</h1>
            <p className="text-sm text-muted-foreground mt-1">Let's connect your care records</p>
          </div>
        </div>

        {/* Linking step - auto-checking */}
        {step === "linking" && (
          <div className="flex flex-col items-center gap-4 py-6" data-testid="linking-step">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Looking up your records...</p>
          </div>
        )}

        {/* No auto-match - ask for name and alt email */}
        {step === "no_match" && (
          <form onSubmit={handleAltEmailSubmit} className="space-y-4" data-testid="manual-link-form">
            <div className="bg-accent rounded-xl p-4 text-sm text-foreground leading-relaxed">
              We couldn't automatically link your account. If you registered with a different email than what you use with Elevated Psychiatry, enter your IntakeQ email below.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                data-testid="onboard-firstname"
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="h-12"
                required
              />
              <Input
                data-testid="onboard-lastname"
                type="text"
                placeholder="Last name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="h-12"
                required
              />
            </div>
            <Input
              data-testid="onboard-email"
              type="email"
              placeholder="Your IntakeQ email address"
              value={altEmail}
              onChange={e => setAltEmail(e.target.value)}
              className="h-12"
              required
            />
            {error && (
              <p className="text-sm text-destructive flex items-center gap-1.5" data-testid="onboard-error">
                <AlertCircle className="w-4 h-4 shrink-0" /> {error}
              </p>
            )}
            <Button
              data-testid="send-code-button"
              type="submit"
              className="w-full h-12 rounded-full"
              disabled={loading || !firstName.trim() || !lastName.trim() || !altEmail.trim()}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Verification Code <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
            <button
              type="button"
              onClick={handleSkip}
              className="text-xs text-muted-foreground underline w-full text-center hover:text-foreground transition-colors"
              data-testid="skip-onboarding-button"
            >
              Skip for now - I'll connect later
            </button>
          </form>
        )}

        {/* Verify code */}
        {step === "verify" && (
          <form onSubmit={handleVerify} className="space-y-4" data-testid="verify-code-form">
            <div className="bg-accent rounded-xl p-4 text-sm text-foreground leading-relaxed">
              A 6-digit verification code was sent to <strong>{altEmail}</strong>. Check your email and enter it below.
            </div>

            {debugCode && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center" data-testid="debug-code-display">
                <p className="text-xs text-blue-600 font-medium uppercase tracking-widest mb-1">Your Verification Code</p>
                <p className="text-3xl font-bold text-blue-800 font-mono tracking-[0.3em]">{debugCode}</p>
                <p className="text-xs text-blue-500 mt-1">Email delivery will activate once your domain is verified in Resend</p>
              </div>
            )}

            <Input
              data-testid="verification-code-input"
              type="text"
              placeholder="123456"
              value={code}
              onChange={e => setCode(e.target.value)}
              className="h-12 text-center text-2xl font-mono tracking-[0.3em]"
              maxLength={6}
              required
            />
            {error && (
              <p className="text-sm text-destructive flex items-center gap-1.5" data-testid="verify-error">
                <AlertCircle className="w-4 h-4 shrink-0" /> {error}
              </p>
            )}
            <Button
              data-testid="verify-code-button"
              type="submit"
              className="w-full h-12 rounded-full"
              disabled={loading || code.length < 6}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
            </Button>
            <button
              type="button"
              onClick={() => { setStep("no_match"); setError(""); setCode(""); setDebugCode(""); }}
              className="text-xs text-muted-foreground underline w-full text-center hover:text-foreground transition-colors"
              data-testid="use-different-email-button"
            >
              Use a different email
            </button>
          </form>
        )}

        {/* Done */}
        {step === "done" && (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="onboarding-done">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <p className="text-sm font-medium text-foreground">Records linked! Redirecting...</p>
          </div>
        )}
      </div>
    </div>
  );
}
